import axios, { AxiosInstance } from 'axios';
import { request } from '../utils';

// ============================== [配置接口] ==============================

// 配置來源類型
export type ConfigSource = 'env' | 'localStorage' | 'direct';

// 系統配置接口
export interface SystemConfig {
  serverUrl?: string;
  apiKey?: string;
  configSource?: ConfigSource;
}

// 認證配置選項
export interface AuthConfig {
  mode: 'jwt' | 'password' | 'token' | 'custom';
  endpoints?: {
    token?: string;        // JWT token 端點
    serverUrl?: string;    // 動態服務器URL端點
    login?: string;        // 登錄端點
  };
  credentials?: {
    username?: string;
    password?: string;
    staticToken?: string;   // 固定 token
  };
  serverUrl?: string;       // 固定服務器 URL
  apiKey?: string;         // API Key
  customAuthFn?: () => Promise<{ accessToken: string; serverUrl: string }>;
}

// 認證結果接口
export interface AuthResult {
  accessToken: string;
  serverUrl: string;
}

// ============================== [配置管理] ==============================

// 獲取系統配置
const getSystemConfig = (): SystemConfig => {
  // 1. 嘗試從環境變數獲取
  const envServerUrl = process.env.REACT_APP_SERVER_URL;
  const envApiKey = process.env.REACT_APP_API_KEY;

  if (envServerUrl || envApiKey) {
    return {
      serverUrl: envServerUrl,
      apiKey: envApiKey,
      configSource: 'env'
    };
  }

  // 2. 嘗試從 localStorage 獲取
  try {
    const envConfig = localStorage.getItem('env_config');
    if (envConfig) {
      const config = JSON.parse(envConfig);
      return {
        serverUrl: config.server_url,
        apiKey: config.api_key,
        configSource: 'localStorage'
      };
    }
  } catch (error) {
    console.warn('從 localStorage 讀取配置失敗:', error);
  }

  // 3. 返回空配置
  return {
    configSource: 'direct'
  };
};

// ============================== [認證策略函數] ==============================

// JWT 認證策略
const jwtAuth = async (config: AuthConfig): Promise<AuthResult> => {
  try {
    const sysConfig = getSystemConfig();
    const baseUrl = config.serverUrl || sysConfig.serverUrl;
    const apiKey = config.apiKey || sysConfig.apiKey;

    if (!baseUrl) {
      throw new Error('Server URL is required for JWT auth');
    }

    const tokenEndpoint = config.endpoints?.token || '/private/property/official_site/jwt';
    const serverUrlEndpoint = config.endpoints?.serverUrl || '/private/property/official_site/profile';

    // 設置請求頭
    const headers: Record<string, string> = {
      'accept': 'application/json'
    };

    // 如果有 API Key，添加到請求頭
    if (apiKey) {
      headers['x-api-key'] = apiKey;
    }

    // 並行獲取 token 和 serverUrl
    const [tokenResponse, serverResponse] = await Promise.all([
      request.get(`${baseUrl}${tokenEndpoint}`, { headers }),
      request.get(`${baseUrl}${serverUrlEndpoint}`, { headers })
    ]);

    return {
      accessToken: tokenResponse.data.access_token,
      serverUrl: serverResponse.data.body?.server_url || baseUrl
    };
  } catch (error) {
    console.error('JWT 認證失敗:', error);
    throw new Error('JWT authentication failed');
  }
};

// 用戶名密碼認證策略
const passwordAuth = async (config: AuthConfig): Promise<AuthResult> => {
  try {
    // console.log('passwordAuth config:', config);
    const baseUrl = config.serverUrl;
    if (!baseUrl) {
      throw new Error('AUTH_SERVER_URL or login endpoint base URL is required for password auth');
    }

    const loginEndpoint = config.endpoints?.login || '/auth/login';
    const credentials = config.credentials;

    if (!credentials?.username || !credentials?.password) {
      throw new Error('Username and password are required for password auth');
    }

    const formData = new URLSearchParams();
    formData.append('grant_type', 'password');
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);

    const response = await request.post(`${baseUrl}${loginEndpoint}`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    // 如果有配置 serverUrl 端點，動態獲取；否則使用認證服務器作為業務服務器
    let serverUrl = config.serverUrl;
    if (!serverUrl && config.endpoints?.serverUrl) {
      const serverResponse = await request.get(`${baseUrl}${config.endpoints.serverUrl}`);
      serverUrl = serverResponse.data.body?.server_url || serverResponse.data.server_url;
    }

    // 如果還是沒有 serverUrl，使用認證服務器作為業務服務器（向後兼容）
    if (!serverUrl) {
      serverUrl = baseUrl;
    }

    return {
      accessToken: response.data.access_token,
      serverUrl
    };
  } catch (error) {
    console.error('密碼認證失敗:', error);
    throw new Error('Password authentication failed');
  }
};

// 靜態 Token 認證策略
const tokenAuth = async (config: AuthConfig): Promise<AuthResult> => {
  try {
    const { staticToken } = config.credentials || {};
    const serverUrl = config.serverUrl;

    if (!staticToken) {
      throw new Error('Static token is required for token auth mode');
    }
    if (!serverUrl) {
      throw new Error('Server URL is required for token auth mode');
    }

    return {
      accessToken: staticToken,
      serverUrl
    };
  } catch (error) {
    console.error('Token 認證失敗:', error);
    throw new Error('Token authentication failed');
  }
};

// 認證策略工廠
const authenticate = async (config: AuthConfig): Promise<AuthResult> => {
  switch (config.mode) {
    case 'jwt':
      return await jwtAuth(config);
    case 'password':
      return await passwordAuth(config);
    case 'token':
      return await tokenAuth(config);
    case 'custom':
      if (!config.customAuthFn) {
        throw new Error('Custom auth function is required for custom auth mode');
      }
      return await config.customAuthFn();
    default:
      throw new Error(`Unsupported auth mode: ${config.mode}`);
  }
};

// ============================== [認證狀態管理] ==============================
// 全局認證狀態
let authState = {
  isAuthenticated: false,
  authConfig: null as AuthResult | null,
  authPromise: null as Promise<AuthResult> | null
};

// 獲取完整認證配置
export const getAuthConfig = async (authConfig?: AuthConfig) => {
  try {
    const config = authConfig || getDefaultAuthConfig();
    return await authenticate(config);
  } catch (error) {
    console.error('獲取認證配置失敗:', error);
    throw new Error('Failed to fetch authentication configuration');
  }
};

// 獲取默認認證配置
const getDefaultAuthConfig = (): AuthConfig => {
  const sysConfig = getSystemConfig();
  return {
    mode: 'jwt',
    serverUrl: sysConfig.serverUrl,
    apiKey: sysConfig.apiKey,
    endpoints: {
      token: '/private/property/official_site/jwt',
      serverUrl: '/private/property/official_site/profile'
    }
  };
};

// 兼容性函數 - 獲取臨時 Access Token (備用認證方案)
export const getTemporaryAccessToken = async () => {
  const config: AuthConfig = {
    mode: 'password',
    endpoints: {
      login: '/auth/login'
    }
  };

  const result = await passwordAuth(config);
  return result.accessToken;
};

// 檢查認證狀態
export const isAuthenticated = () => authState.isAuthenticated;

// ============================== [Axios 實例配置] ==============================

// 創建業務 API 客戶端實例
const createBusinessApiClient = (authResult: AuthResult): AxiosInstance => {
  const client = axios.create({
    baseURL: authResult.serverUrl, // 🚀 使用動態獲取的業務服務器URL
    timeout: 30000,
    headers: {
      'Authorization': `Bearer ${authResult.accessToken}`, // 🚀 使用新的認證token
      'Content-Type': 'application/json'
    }
  });

  // 請求攔截器
  client.interceptors.request.use((config) => {
    // 如果是 FormData，刪除 Content-Type 讓瀏覽器自動設置
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  }, (error) => {
    console.error('API 請求錯誤:', error);
    return Promise.reject(error);
  });

  // 響應攔截器
  client.interceptors.response.use((response) => {
    response.data.status = response.status;
    return response;
  }, (error) => {
    // 如果是認證錯誤，清除認證狀態
    if (error.response?.status === 401) {
      console.log('認證過期，清除認證狀態');
      clearAuth();
    } else {
      // 只記錄非認證錯誤
      console.error('API 響應錯誤:', error.message);
    }

    if (error.response) {
      error.response.data.status = error.response.status;
      return Promise.reject(error.response.data);
    }
    return Promise.reject(error);
  });

  return client;
};

// ============================== [主要導出] ==============================

// 創建 API 客戶端，支援配置化認證
export const createApiClient = async (authConfig?: AuthConfig): Promise<AxiosInstance> => {
  try {
    let authResult: AuthResult;

    if (authConfig) {
      // 🚀 使用提供的認證配置
      authResult = await authenticate(authConfig);
    } else {
      // 🚀 使用默認認證 + 狀態管理（向後兼容）
      authResult = await ensureAuthenticated();
    }

    // 🚀 直接返回 axios 實例，錯誤處理已在 interceptor 中
    return createBusinessApiClient(authResult);
  } catch (error) {
    console.error('創建 API 客戶端失敗:', error);
    throw new Error('Failed to create API client');
  }
};

// 簡單的認證檢查：如果沒認證就去認證（使用默認配置）
const ensureAuthenticated = async (): Promise<AuthResult> => {
  // 如果已經認證過，直接返回
  if (authState.isAuthenticated && authState.authConfig) {
    console.log('✅ 已認證，直接使用');
    return authState.authConfig;
  }

  // 如果正在認證中，等待完成
  if (authState.authPromise) {
    return authState.authPromise;
  }

  // 開始新的認證流程
  authState.authPromise = getAuthConfig(); // 使用默認配置

  try {
    const authConfig = await authState.authPromise;
    authState.isAuthenticated = true;
    authState.authConfig = authConfig;
    authState.authPromise = null;
    return authConfig;
  } catch (error) {
    authState.authPromise = null;
    console.error('認證失敗:', error);
    throw error;
  }
};

// 清除認證狀態
export const clearAuth = () => {
  authState.isAuthenticated = false;
  authState.authConfig = null;
  authState.authPromise = null;
};